import { isResource } from '@medplum/core'
import type {
  CodeableConcept,
  Composition,
  Patient,
  Practitioner,
  Reference,
} from '@medplum/fhirtypes'
import {
  codeableConceptHasSystem,
  hasCodeableConcept,
  systemFromCodeableConcept,
} from '#src/codeableConcept.ts'
import { INCIDENT_TYPE } from '#src/codeSystems.ts'
import { invariant } from '#src/invariant.ts'
import { Assessment } from '#src/models/assessments/assessment.ts'
import { INCIDENT_TYPES } from '#src/models/assessments/constants.ts'
import { isDroppableEmptySection } from '#src/models/compositionLibraryEncoded.ts'
import { CompositionCategory } from '#src/models/compositions/constants.ts'
import type { IncidentTemplate } from '#src/models/incidentTemplate.ts'
import type { QueryTypes } from '#src/search.ts'
import { generateUUID, isOfType, now } from '#src/utils.ts'

export class Incident extends Assessment {
  constructor(
    composition: Omit<Composition, 'resourceType' | 'date' | 'status'> &
      Partial<Pick<Composition, 'date' | 'status'>>
  ) {
    super({
      ...composition,
      category: [CompositionCategory.Incident],
    })
  }

  static buildFromTemplate(
    practitioner: Reference<Practitioner>,
    participant: Reference<Patient>,
    template: IncidentTemplate
  ): Incident {
    const incident = new Incident({
      title: template.title,
      type: INCIDENT_TYPES.INCIDENT_LEVEL_ONE,
      author: [practitioner],
    })
    incident.subject = participant
    incident.template = template.reference
    incident.startsAt = now()
    incident.sections = template.sections
      .filter((section) => !isDroppableEmptySection(section))
      .map((section) => ({
        ...section,
        id: generateUUID(),
      }))
    return incident
  }

  set type(type: CodeableConcept) {
    invariant(
      systemFromCodeableConcept(type) === INCIDENT_TYPE,
      'Incident type coding system must match INCIDENT_TYPE'
    )
    this.resource.type = type
  }

  get type(): CodeableConcept {
    return this.resource.type
  }

  static isInstance(value: unknown): value is Composition {
    return (
      isResource(value) &&
      isOfType(value, 'Composition') &&
      hasCodeableConcept(value.category, CompositionCategory.Incident) &&
      codeableConceptHasSystem(value.type, INCIDENT_TYPE)
    )
  }

  static searchQuery({
    practitioner,
    subject,
  }: {
    practitioner?: Reference<Practitioner>
    subject?: Reference<Patient>
  }): QueryTypes {
    const query: QueryTypes = {}
    if (practitioner) {
      query.author = practitioner.reference
    }
    if (subject) {
      query.subject = subject.reference
    }
    query._sort = '-_lastUpdated'
    query.category = CompositionCategory.Incident
    query['status:not'] = 'entered-in-error'
    return query
  }

  /**
   * Base FHIR search query for this resource — extended/modified at call sites.
   * Changing this affects every caller. Grep `baseSearchQuery` for impact before editing.
   */
  static baseSearchQuery(): QueryTypes {
    return this.searchQuery({})
  }
}
